import type { CSSProperties } from 'react';
import { useSettings } from '../../state/settingsStore.ts';
import { useReducedMotion } from '../../systems/motion.ts';
import styles from './Banknotes.module.css';

/** A small, stable number in [0, 1) for note `i` and a stream `salt`: the same shower every time. */
export function unit(i: number, salt: number): number {
  let h = Math.imul(i + 1, 374761393) ^ Math.imul(salt + 1, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * Banknotes drifting down over what is behind them: a few while the loot is being divided, a
 * shower (`burst`) when the split is agreed. Nothing for players who turned particles off or
 * asked for less motion; the numbers are always in the text.
 */
export function Banknotes({ count = 7, burst = false }: { count?: number; burst?: boolean }) {
  const particles = useSettings().graphics.particles;
  const reduced = useReducedMotion();
  if (!particles || reduced) return null;
  const n = burst ? count * 4 : count;
  return (
    <div className={styles['sky']} aria-hidden="true">
      {Array.from({ length: n }, (_, i) => (
        <span
          key={i}
          className={[styles['note'], burst ? styles['burst'] : ''].join(' ')}
          style={
            {
              '--x': `${(unit(i, 1) * 100).toFixed(1)}%`,
              '--delay': `${(unit(i, 2) * (burst ? 0.8 : 3.5)).toFixed(2)}s`,
              '--dur': `${(burst ? 2.2 : 5) + unit(i, 3) * 2.5}s`,
              '--spin': `${((unit(i, 4) - 0.5) * 720).toFixed(0)}deg`,
              '--sway': `${((unit(i, 5) - 0.5) * 4).toFixed(1)}rem`,
              '--scale': (0.75 + unit(i, 6) * 0.5).toFixed(2),
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
